import { useMemo, useRef } from "react";
import tw from "twin.macro";
import Heading from "@atoms/Heading";
import Modal from "@atoms/Modal";
import Button from "@atoms/Button";
import IconClose from "@iconsV2/Edit/close.svg?react";

import {
  StyledContent,
  StyledContentAllProducts,
  StyledContentBottom,
  StyledContentHeading,
  StyledIconShoppingCart,
  StyledLoaderLoading,
  StyledWrapperLoader,
} from "./styles";
import { CheckoutEnum } from "@/recourses/queryKeys";
import { useQuery } from "@tanstack/react-query";
import useCart from "@/hooks/useCartV2";
import { getCartAvailableProducts } from "@/services/checkout";
import RecomendedOneProduct from "./RecomendedOneProduct";
import RecomendedVariusProducts from "./RecomendedVariusProducts";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Notification from "@/components/Atoms/Notification";
import useAppSelector from "@/hooks/useAppSelector";
import { capitalize } from "lodash";

interface ModalItemsOutOfStockProps {
  isOpen: boolean;
  handleClose: () => void;
  onSubmit?: () => void;
  storeId?: number;
  onlyClose?: boolean;
  closeOutSise?: boolean;
  productToVerify?: Cart;
  isOrderRecommendation?: boolean;
}

const ModalItemsOutOfStock = ({
  isOpen,
  handleClose,
  storeId,
  onSubmit,
  onlyClose = false,
  closeOutSise = true,
  productToVerify,
  isOrderRecommendation = false,
}: ModalItemsOutOfStockProps) => {
  const { items, handleRemoves } = useCart();
  const isMedium = useMediaQuery("(min-width: {medium})");
  const currentStore = useAppSelector((state) => state.cart.location);

  const itemsRef = useRef(items);

  const { data: cartAvailables, isFetching } = useQuery({
    queryKey: [CheckoutEnum.getCartAvailableProducts, storeId, JSON.stringify(itemsRef.current)],
    queryFn: async () => {
      const productsId = itemsRef.current.map((item) => String(item.product.sku)).join(",");
      return await getCartAvailableProducts(Number(storeId), productsId);
    },
    enabled: isOpen && !productToVerify,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
  });

  const productsWithAvailabilityInfo = useMemo(() => {
    if (!cartAvailables?.payload || productToVerify) return [];

    return itemsRef.current.map((item) => {
      const availabilityInfo = cartAvailables.payload.find((availableItem) => {
        return String(availableItem.product.sku) === String(item.product.sku);
      });

      return {
        ...item,
        details: {
          ...item.details,
          available: availabilityInfo?.is_available ?? false,
        },
      };
    });
  }, [itemsRef.current, cartAvailables?.payload, productToVerify?.product?.sku]);

  const productsOutOfStock = useMemo(() => {
    if (productToVerify) {
      return [productToVerify];
    }

    return productsWithAvailabilityInfo.filter((item) => !item.details.available);
  }, [productsWithAvailabilityInfo, productToVerify?.product?.sku]);

  const isOneProduct = productsOutOfStock.length === 1;

  const title = useMemo(() => {
    if (isOrderRecommendation) {
      return `Este producto no está disponible en ${capitalize(currentStore.city)}`;
    }

    return isOneProduct
      ? "Este producto no está disponible por el momento"
      : `Estos ${productsOutOfStock.length} productos no están disponibles por el momento`;
  }, [isOneProduct, isOrderRecommendation, productsOutOfStock.length]);

  const handleConinue = async () => {
    handleRemoves(
      productsOutOfStock.map((item) => String(item?.product?.sku)),
      () => {
        onSubmit?.();
        handleClose();
      }
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => (onlyClose ? handleClose() : onSubmit?.())}
      closeOutSise={closeOutSise}
      fullScreen={!isMedium}
      classes={{
        card: tw`h-screen p-0 w-full flex flex-col medium:(max-w-[35rem] h-auto max-h-[80vh]) large:(max-h-[42rem]) relative`,
        backdrop: tw`z-[53]`,
      }}
    >
      {isFetching && (
        <StyledWrapperLoader>
          <StyledLoaderLoading />
        </StyledWrapperLoader>
      )}
      <StyledContent tw="flex flex-row h-max sticky top-0">
        <StyledContentHeading>
          <StyledIconShoppingCart />
          <Heading type="h5" tw="text-sm">
            {title}
          </Heading>
        </StyledContentHeading>

        {closeOutSise && (
          <Button
            type="button"
            sizeVariant="lg"
            variant="text"
            color="secondary"
            startIcon={<IconClose tw="h-6 w-6 cursor-pointer" />}
            onClick={() => (onlyClose ? handleClose() : onSubmit?.())}
          />
        )}
      </StyledContent>

      {isOneProduct ? (
        <RecomendedOneProduct product={productsOutOfStock?.[0]} storeId={storeId!} items={itemsRef.current} />
      ) : (
        <StyledContentAllProducts>
          {productsOutOfStock.map((item) => (
            <RecomendedVariusProducts
              key={item.product.id}
              product={item}
              storeId={storeId!}
              items={itemsRef.current}
            />
          ))}
        </StyledContentAllProducts>
      )}

      {!productToVerify && (
        <StyledContentBottom>
          <Notification
            type="warning"
            messages="Los productos sin disponibilidad serán eliminados de tu carrito de compras"
          />
          <Button sizeVariant="md" variant="outlined" color="secondary" onClick={handleConinue}>
            Continuar
          </Button>
        </StyledContentBottom>
      )}
    </Modal>
  );
};

export default ModalItemsOutOfStock;
